import { useEffect, useState } from 'react';

export default function Hero() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/9b25515fa54d983d616b5158f158fac9.jpg"
          alt="Air Separation Unit Industrial Facility"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-900/85 via-stone-800/70 to-stone-900/60"></div>
      </div>

      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-stone-100/95 backdrop-blur-md shadow-lg'
            : 'bg-transparent'
        }`}
      >
        <div className="flex items-center justify-center px-4 md:px-8 lg:px-12 py-3 md:py-4">
          <div className="flex items-center gap-3 md:gap-6 lg:gap-10 flex-wrap justify-center">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className={`${scrolled ? 'text-stone-700 hover:text-teal-600' : 'text-stone-200 hover:text-teal-400'} text-xs md:text-sm font-semibold tracking-wide transition-colors cursor-pointer`}
            >
              Home
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className={`${scrolled ? 'text-stone-700 hover:text-teal-600' : 'text-stone-200 hover:text-teal-400'} text-xs md:text-sm font-semibold tracking-wide transition-colors cursor-pointer`}
            >
              Infrastructure
            </button>
            <button
              onClick={() => scrollToSection('products')}
              className={`${scrolled ? 'text-stone-700 hover:text-teal-600' : 'text-stone-200 hover:text-teal-400'} text-xs md:text-sm font-semibold tracking-wide transition-colors cursor-pointer`}
            >
              Deployments
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className={`${scrolled ? 'text-stone-700 hover:text-teal-600' : 'text-stone-200 hover:text-teal-400'} text-xs md:text-sm font-semibold tracking-wide transition-colors cursor-pointer`}
            >
              Capabilities
            </button>
            <button
              onClick={() => scrollToSection('testimonials')}
              className={`${scrolled ? 'text-stone-700 hover:text-teal-600' : 'text-stone-200 hover:text-teal-400'} text-xs md:text-sm font-semibold tracking-wide transition-colors cursor-pointer`}
            >
              Impact
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white px-4 md:px-6 py-2 md:py-2.5 rounded-full text-xs md:text-sm font-semibold transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-teal-500/25"
            >
              Partner With Us
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="relative z-10 flex flex-col items-center text-center w-full px-4 md:px-8 lg:px-12 pt-20 md:pt-24 pb-12 md:pb-20 min-h-screen">
        {/* Logo Banner - Centered Top */}
        <div className="mt-2 md:mt-4 mb-6 md:mb-10">
          <img
            src="https://static.readdy.ai/image/dadfa7b8e8bbab8ae3e8df395caac6f8/106d52227b4af173f8dda8770a67c446.png"
            alt="SkyForge Industrial Gases"
            className="w-48 md:w-72 lg:w-96 h-auto drop-shadow-2xl"
          />
        </div>

        {/* Infrastructure Badges */}
        <div className="flex items-center gap-2 md:gap-4 mb-6 md:mb-8 flex-wrap justify-center">
          <div className="flex items-center gap-2 bg-stone-100/90 backdrop-blur-sm border border-teal-300 rounded-full px-3 md:px-5 py-2 md:py-2.5 shadow-lg">
            <i className="ri-building-4-line text-teal-600 text-sm md:text-lg"></i>
            <span className="text-teal-700 text-xs md:text-sm font-bold">ASU Deployment</span>
          </div>
          <div className="w-2 h-2 bg-teal-400 rounded-full shadow-lg shadow-teal-400/50 hidden md:block"></div>
          <div className="flex items-center gap-2 bg-stone-100/90 backdrop-blur-sm border border-cyan-300 rounded-full px-3 md:px-5 py-2 md:py-2.5 shadow-lg">
            <i className="ri-ship-line text-cyan-600 text-sm md:text-lg"></i>
            <span className="text-cyan-700 text-xs md:text-sm font-bold">Smart Port Integration</span>
          </div>
          <div className="w-2 h-2 bg-cyan-400 rounded-full shadow-lg shadow-cyan-400/50 hidden md:block"></div>
          <div className="flex items-center gap-2 bg-stone-100/90 backdrop-blur-sm border border-teal-300 rounded-full px-3 md:px-5 py-2 md:py-2.5 shadow-lg">
            <i className="ri-global-line text-teal-600 text-sm md:text-lg"></i>
            <span className="text-teal-700 text-xs md:text-sm font-bold">Global Trade Corridors</span>
          </div>
        </div>

        {/* Main Heading */}
        <h1 className="text-stone-100 font-black leading-tight mb-4 md:mb-6 text-3xl md:text-5xl lg:text-6xl px-2">
          Global Industrial Gas &
          <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-cyan-400">
            Smart Infrastructure
          </span>
          <br />
          Development Platform
        </h1>

        {/* Description */}
        <p className="text-stone-300 text-sm md:text-base lg:text-lg leading-relaxed max-w-3xl mb-6 md:mb-10 px-2">
          Next-generation industrial gas production and infrastructure development company deploying Air Separation Units (ASUs) and integrated smart logistics ecosystems across strategic global trade corridors.
        </p>

        {/* Tagline */}
        <div className="mb-6 md:mb-10">
          <p className="text-transparent bg-clip-text bg-gradient-to-r from-teal-300 to-cyan-300 text-lg md:text-xl lg:text-2xl font-bold tracking-wide">
            Infrastructure. Intelligence. Industrial Power.
          </p>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col md:flex-row items-center gap-3 md:gap-4 mb-10 md:mb-16 w-full max-w-md md:max-w-none px-4">
          <button
            onClick={() => scrollToSection('contact')}
            className="w-full md:w-auto bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white px-6 md:px-8 py-3 md:py-4 rounded-full font-semibold text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow-xl shadow-teal-500/40"
          >
            Infrastructure Inquiries
            <i className="ri-arrow-right-line text-lg"></i>
          </button>
          <button
            onClick={() => scrollToSection('products')}
            className="w-full md:w-auto bg-stone-100/90 backdrop-blur-sm border-2 border-stone-200 hover:border-teal-400 text-stone-700 hover:text-teal-600 px-6 md:px-8 py-3 md:py-4 rounded-full font-semibold text-sm transition-all cursor-pointer whitespace-nowrap shadow-lg"
          >
            View Deployments
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:flex md:items-center gap-3 md:gap-6 w-full max-w-2xl md:max-w-none px-4">
          <div className="bg-stone-100/90 backdrop-blur-sm rounded-2xl px-4 md:px-8 py-4 md:py-6 shadow-xl border border-stone-200">
            <p className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-cyan-600 text-2xl md:text-3xl font-black">
              4
            </p>
            <p className="text-stone-600 text-xs md:text-sm mt-1">Global Regions</p>
          </div>
          <div className="bg-stone-100/90 backdrop-blur-sm rounded-2xl px-4 md:px-8 py-4 md:py-6 shadow-xl border border-stone-200">
            <p className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-cyan-600 text-2xl md:text-3xl font-black">
              5+
            </p>
            <p className="text-stone-600 text-xs md:text-sm mt-1">ASU Deployments</p>
          </div>
          <div className="bg-stone-100/90 backdrop-blur-sm rounded-2xl px-4 md:px-8 py-4 md:py-6 shadow-xl border border-stone-200">
            <p className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-cyan-600 text-2xl md:text-3xl font-black">
              AI
            </p>
            <p className="text-stone-600 text-xs md:text-sm mt-1">Smart Logistics</p>
          </div>
          <div className="bg-stone-100/90 backdrop-blur-sm rounded-2xl px-4 md:px-8 py-4 md:py-6 shadow-xl border border-stone-200">
            <p className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-cyan-600 text-2xl md:text-3xl font-black">
              24/7
            </p>
            <p className="text-stone-600 text-xs md:text-sm mt-1">Global Operations</p>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10 hidden md:flex">
        <span className="text-stone-400 text-xs tracking-widest uppercase">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-teal-400 to-transparent"></div>
      </div>
    </section>
  );
}
